import React, {
  ClipboardEvent,
  createRef,
  KeyboardEvent,
  useEffect
} from 'react';

import { ComponentProps } from '../utils/types';
import { keyCode, patterns } from './constants';
import { DefaultInputStyle, PinInputContainer } from './styles';
import PinInputPropsInterface from './types';
import { getFocusPosition, padEnd } from './utils';

const PinInput: React.FC<PinInputPropsInterface> = ({
  id,
  type = 'text',
  value: rawStateValues,
  onChange,
  autoFocus = true,
  pattern,
  disabled = false,
  required,
  error = false,
  length = 6,
  mask = false,
  ...otherProps
}) => {
  const stateValues = padEnd(rawStateValues.split(''), length);
  const maskChar = error ? '◦' : '●';
  const maskedStateValues = stateValues.map((val) =>
    val !== '' ? maskChar : ''
  );
  const focusIndex = autoFocus ? getFocusPosition(stateValues) : -1;
  const refs = Array.from({ length: stateValues.length }).map(() =>
    createRef<HTMLInputElement>()
  );
  const checkPattern = pattern || patterns[type];

  useEffect(() => {
    stateValues.forEach((value, index) => {
      const ref = refs[index]?.current;
      if (ref) {
        ref.value = mask && value ? maskChar : value;
      }
    });
  }, [refs, stateValues, mask, maskChar]);

  const handleChange = (value: string, index: number) => {
    const changedValues = [...stateValues];

    changedValues[index] = value;
    onChange(changedValues.join(''));
  };

  const onChangeAll = (values: Array<string>) => {
    for (const [key, value] of values.entries()) {
      if (checkPattern && !checkPattern.test(value)) {
        values[key] = '';
      }
    }

    onChange(values.join(''));
  };

  /**
   * Handle input paste event
   */
  const handlePaste = (e: ClipboardEvent<HTMLInputElement>, index: number) => {
    e.preventDefault();
    const values = [...stateValues];
    const clipboardValues = e.clipboardData.getData('Text').split('');
    for (const [key, value] of clipboardValues.entries()) {
      const ref = refs[index + key]?.current;
      if (!ref) {
        break;
      }

      values[index + key] = value;
      const next = refs[index + key + 1]?.current;
      if (next) {
        next.focus();
        continue;
      }

      ref.focus();
    }

    onChangeAll(values);
  };

  /**
   * Handle input key down
   */
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    const current = refs[index].current;
    const next = refs[index + 1]?.current;
    const prev = refs[index - 1]?.current;
    const isValidKey = checkPattern && checkPattern.test(e.key);

    if (!(e.ctrlKey || e.metaKey)) {
      e.preventDefault();
    }

    switch (e.key) {
      case keyCode.BACKSPACE:
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          onChangeAll(Array<string>(stateValues.length).fill(''));
          refs[0].current?.focus();
        } else if (current?.value !== '') {
          handleChange('', index);
        } else if (current?.value === '' && index > 0) {
          handleChange('', index - 1);
          prev?.focus();
        }
        break;
      case keyCode.LEFT_ARROW:
      case keyCode.LEFT_ARROW_COMPATIBILITY:
      case keyCode.UP_ARROW:
      case keyCode.UP_ARROW_COMPATIBILITY:
        prev?.focus();
        break;
      case keyCode.RIGHT_ARROW:
      case keyCode.RIGHT_ARROW_COMPATIBILITY:
      case keyCode.DOWN_ARROW:
      case keyCode.DOWN_ARROW_COMPATIBILITY:
        next?.focus();
        break;
      default:
        if (current?.value === e.key) {
          next?.focus();
        } else if (current && isValidKey && !e.ctrlKey && !e.metaKey) {
          handleChange(e.key, index);
          next?.focus();
        }
    }
  };

  return (
    <PinInputContainer id={id} {...otherProps}>
      {refs.map((ref, index) => (
        <DefaultInputStyle
          key={`${id}-${index}`}
          id={`${id}-${index}`}
          data-testid={`pin-input-${index}`}
          type="text"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={index === focusIndex}
          value={mask ? maskedStateValues[index] : stateValues[index]}
          ref={ref}
          onChange={(e) => handleChange(e.currentTarget.value, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={(e) => handlePaste(e, index)}
          disabled={disabled}
          required={required}
          autoComplete="off"
          error={error}
          inputMode={type === 'number' ? 'numeric' : undefined}
        />
      ))}
    </PinInputContainer>
  );
};

export type PinInputProps = ComponentProps<typeof PinInput>;
export default PinInput;
